/**
 * What the plan screen has to say next, and whether research is one of the things it offers.
 *
 * **Its own module, with no DOM and no React in it, for `chat/keyboard.ts`'s reason**: this is
 * the decision F-118 can most easily get wrong, and a decision that cannot be tested is one
 * that gets argued about instead. The screen it drives is a thousand lines of JSX that needs a
 * browser and a renderer to render at all, so the judgement lives here and
 * `packages/web/test/plan-step.test.ts` asserts it from plain Node.
 *
 * The failure it exists to prevent is not cosmetic. A learner who chose "I have the material"
 * must never be offered the research button — not because pressing it would work (the API
 * refuses, the pipeline refuses again), but because offering it is us telling somebody their own
 * choice did not take, and the refusal they would read is a sentence about a state they did not
 * ask to be in. The mirror of that is the state Leo named directly: uploads-only with nothing
 * uploaded yet is a person about to take photographs, not a broken objective, so it gets a
 * sentence asking for the pages rather than a spinner or a complaint.
 */

/**
 * The one thing the plan screen offers at this moment.
 *
 * One value rather than five booleans, because the lifecycle is a line and every status has
 * exactly one next move. Two of these on screen together is what put a greyed-out research
 * button beside the thing the learner was supposed to press, and it is why the panel was made
 * exclusive in the first place.
 */
export type PlanStep =
  /** A researched objective that has not asked the world yet: offer the pass. */
  | 'research'
  /**
   * An objective with no plan whose plan comes from the learner's pages: ask for them.
   *
   * The one state F-118 added, and the one Leo called out by name. It is not an error and not a
   * wait — nothing is happening and nothing is supposed to be, until the learner adds something
   * — so the panel says what to do rather than reporting a condition.
   *
   * **A `scoped` objective rests here too, and that is the decision F-119 had to get right on
   * this screen.** It does research, so the obvious thing is to offer the research button — and
   * offering it would be wrong twice over: the API refuses it, deliberately, because an outline
   * written from an empty content store is the open-web pass the mode exists to refuse; and
   * what the learner actually has to do is add a page, after which the research starts on its
   * own. A button whose only outcome is a refusal, beside the thing they were supposed to press,
   * is the state the panel was made exclusive to prevent.
   */
  | 'add_material'
  /** A research pass is running. Unreachable for an uploads-only objective, by construction. */
  | 'researching'
  /** A curriculum is being built, whatever filled the content store. */
  | 'building'
  /** There is material and no plan: offer to build one. */
  | 'build'
  /** Nothing to say here — the plan exists and the rest of the screen is about it. */
  | 'none';

/** As much of an objective row as this decision needs, in the shape the browser holds it. */
export interface PlanShape {
  status: string;
  /**
   * The stored value, raw and possibly absent.
   *
   * Absent means research, exactly as `planSourceOf` decides it on the server. Duplicated here
   * as one comparison rather than imported because the browser holds a plain JSON row and the
   * default must be the same one in both places; the test below asserts the absent case
   * explicitly so the two cannot drift apart silently.
   */
  planFrom?: string;
}

/** Whether this objective builds its plan only from what the learner adds. */
export function isUploadsOnly(objective: PlanShape | null | undefined): boolean {
  return objective?.planFrom === 'uploads';
}

/**
 * Whether this objective's first plan comes from the pages the learner adds. F-119.
 *
 * `uploads` and `scoped` both, and the mirror of `buildsFirstPlanFromUploads` on the server —
 * which is the function that decides the same thing about the fold. Duplicated as one comparison
 * rather than imported for `PlanShape`'s reason: the browser holds a plain JSON row and the
 * default has to be the same one in both places, with the test asserting the absent case so the
 * two cannot drift apart silently.
 */
export function awaitsOwnMaterial(objective: PlanShape | null | undefined): boolean {
  return objective?.planFrom === 'uploads' || objective?.planFrom === 'scoped';
}

/**
 * Whether the controls that research *the topic* may be shown for this objective.
 *
 * The first pass and the plan-level second look: one outlines the objective text on the open
 * web, the other asks the web what that outline missed. Both are refused for `uploads` and both
 * are refused for `scoped`, and for `scoped` the reason is the mode itself rather than thrift —
 * every subject a gaps pass can propose is by construction one the learner's own material does
 * not cover, which is the syllabus-beside-the-book this mode exists to refuse. `allowsResearch`
 * on the server is where that table actually lives; this is what stops the learner being shown a
 * button whose only possible outcome is being told no.
 *
 * **It deliberately does not cover "go deeper on this subject".** That control is pointed at a
 * subject the learner's own chapter named, so a scoped objective may press it and the API allows
 * it — which is why the research screen gates on having subjects at all rather than on this. An
 * uploads-only objective has no subjects and so never reaches it.
 */
export function offersResearch(objective: PlanShape | null | undefined): boolean {
  return !awaitsOwnMaterial(objective);
}

/**
 * What the screen knows about this objective that its stored status does not say yet.
 *
 * Two facts, and both of them are gaps between an event and the row that records it. F-144 and
 * F-146 are the two halves of the same complaint — that the screen answers to storage a poll
 * behind the learner — and the fix for both is to let the decision see them.
 */
export interface LiveResearch {
  /**
   * The learner has just pressed *start research* and nothing has recorded it yet.
   *
   * Leo, 2026-09-17: *"after click start research there is no visual indication of whats going
   * on."* The API answers the moment the status is written and this screen then reads the
   * objective back through an eventually consistent index, so for up to a poll interval the
   * stored status still says `drafting` — and the panel went on offering the button that had
   * just been pressed, with nothing anywhere saying that twenty minutes of work had started.
   */
  starting?: boolean;
  /**
   * Research is still arriving for this objective, whatever its status says.
   *
   * Leo, 2026-09-17: *"it gives me a build the curriculum while none of the subjects is
   * researched all are in reading."* An objective sits at `review` with no plan for two
   * different reasons — its research finished, or an early fold found too little and the rest
   * of the subjects are still being read (F-114) — and only the run row can tell them apart.
   * **`POST /api/objectives/synthesise` refuses the second one outright** with
   * `still_researching`, which is what makes offering the button a bug rather than a
   * preference: the only thing pressing it can produce is a refusal.
   */
  stillComing?: boolean;
}

/**
 * The step this objective is at.
 *
 * `nodes` is how many curriculum nodes the screen has read, and `undefined` means it has not
 * asked yet — which is deliberately not the same as nought. Treating "not asked" as "no plan"
 * flashes an offer to build one underneath an objective that already has one, which is how the
 * build button appeared and vanished on every load.
 *
 * `live` is what the screen has seen and storage has not caught up with. Both of its fields
 * only ever move the answer *towards* `researching` — nothing here can invent an offer, only
 * withdraw one — which is what keeps a stale or missing reading from being able to strand a
 * learner with no button at all.
 */
export function planStep(
  objective: PlanShape | null | undefined,
  nodes: number | undefined,
  live: LiveResearch = {},
): PlanStep {
  if (!objective) return 'none';
  const { status } = objective;

  if (status === 'drafting') {
    /*
     * The fork this whole feature is, and the reason it is a fork rather than a flag on one
     * panel: the two halves have nothing in common. One offers to go and spend money on the
     * learner's behalf; the other says "your pages build this, add them", and has no action of
     * its own beyond pointing at the uploader.
     *
     * An objective built from the learner's own pages sits here for as long as it has nothing
     * read — there is no pass to move it — and the extraction that produces its first material
     * is what moves it on. So this is a resting state and not a transient, which is exactly why
     * it must read as an invitation. True of `scoped` as well as `uploads`: its research is set
     * off by that same extraction, so before it there is genuinely nothing to press.
     */
    if (awaitsOwnMaterial(objective)) return 'add_material';
    /*
     * The press, before anything has recorded it (F-144).
     *
     * Deliberately *inside* the research branch and not above it: an objective that builds its
     * plan from the learner's own pages has no research button to have been pressed, so a flag
     * arriving there is a screen arguing with itself and the invitation for the pages is still
     * the right answer.
     */
    return live.starting ? 'researching' : 'research';
  }

  if (status === 'researching') return 'researching';
  if (status === 'synthesising') return 'building';

  /*
   * A plan that has not been built yet, whatever was supposed to build it.
   *
   * Reached by an uploads-only objective whose first fold failed, and by a researched one whose
   * early fold found too little (F-114). Both want the same thing — the offer to build from
   * whatever the content store now holds — so both get it, and the panel's own copy says
   * whether anything went wrong.
   */
  if (status === 'review' && nodes === 0) {
    /*
     * Research still arriving means there is nothing a build could be built from yet (F-146).
     *
     * The learner is told what is actually happening instead, which is also what the subjects
     * on the research screen have been saying all along — "all are in reading" — and the plan
     * screen was contradicting.
     */
    return live.stillComing ? 'researching' : 'build';
  }

  return 'none';
}
